'use client';

import BoardListContext from '@/context/apps/board/BoardListContext';
import { useRouter } from '@/routing/useAppRouter';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { BoardPagePropsType } from '@/utils/getBoardPageProps';
import type { NewsCategoryFilterOption } from '@/utils/newsCategoryFilter';
import {
  NEWS_LIST_BROWSE_PATH,
  buildNewsListBrowsePath,
  buildNewsListPagePath,
  selectNewsListQuery,
} from '@/utils/newsListRoutes';

import ResponsiveLayout from '@/components/apps/layouts/responsive/Layout';
import NewsBoardList from '@/components/apps/pages/about/news/List';
import VisuallyHiddenPageTitle from '@/components/apps/ui/a11y/VisuallyHiddenPageTitle';

export default function NewsListPageClient({
  board,
  categoryFilterOptions,
  paginationMode,
}: {
  board: BoardPagePropsType;
  categoryFilterOptions: ReadonlyArray<NewsCategoryFilterOption>;
  paginationMode: 'path' | 'query';
}) {
  const router = useRouter();
  const { t } = useTranslation('common');
  const title = t('뉴스룸');
  const sortedList = {
    ...board.bcListData,
    results: [...board.bcListData.results].sort((a, b) =>
      dayjs(a.uploaded_at).isAfter(b.uploaded_at) ? -1 : 1,
    ),
  };
  const getPageHref = (page: number) => {
    if (paginationMode === 'path') return buildNewsListPagePath(page);

    return buildNewsListBrowsePath({
      ...selectNewsListQuery(router.query),
      page: String(page),
    });
  };

  return (
    <>
      <VisuallyHiddenPageTitle>{title}</VisuallyHiddenPageTitle>
      <BoardListContext.Provider value={{ ...board, bcListData: sortedList }}>
        <ResponsiveLayout
          desktopPadding={{ top: '0', bottom: '200px' }}
          mobilePadding={{ top: '0', bottom: '100px' }}
        >
          <NewsBoardList
            categoryFilterOptions={categoryFilterOptions}
            filterPath={NEWS_LIST_BROWSE_PATH}
            getPageHref={getPageHref}
          />
        </ResponsiveLayout>
      </BoardListContext.Provider>
    </>
  );
}
